// Lời gọi API quản trị Gala (hp-media-server-staging/src/gala/gala-admin.controller.ts, chỉ ADMIN).
// AxiosUtils trả nguyên response của axios (quy ước dự án): dữ liệu nằm ở `.data`.
// Lỗi: AxiosUtils ném nguyên `response.data` (`{ code, message }` với lỗi GALA_*) -> dùng errorText().

import { API } from 'service/api-route';
import AxiosUtils from 'utils/axios-utils';
import type {
  AdminSeat,
  AdminSeatingView,
  AnnounceAllResult,
  AnnounceResult,
  AnnounceStage,
  BlockInput,
  BulkTablesInput,
  CategoryInput,
  CheckinList,
  CheckinQuery,
  EventCounts,
  EventInput,
  GalaBlock,
  GalaCategory,
  GalaEvent,
  GalaEventDetail,
  GalaEventStatus,
  GalaNominee,
  GalaReminder,
  GalaTemplate,
  ManualCheckinResult,
  NomineeCreateInput,
  NomineeOverview,
  NomineePasteResult,
  NomineeUpdateInput,
  PasteSeatResult,
  ReminderInput,
  RenderProgress,
  SeatAssignInput,
  SeedResult,
  SendNowInput,
  StaffBrief,
  TableInput,
  TableWithSeats,
  TemplateInput,
  TicketList,
  TicketQuery,
  TicketUpdateInput,
  UpsertResult,
} from './contract';

const base = (id: number | string) => `${API.galaAdmin}/events/${id}`;

async function unwrap<T>(promise: Promise<any>): Promise<T> {
  const res: any = await promise;
  return res?.data as T;
}

// ---------- Sự kiện ----------

export const listEvents = () => unwrap<GalaEvent[]>(AxiosUtils.get(`${API.galaAdmin}/events`));

export const createEvent = (input: EventInput & { title: string }) =>
  unwrap<GalaEvent>(AxiosUtils.post(`${API.galaAdmin}/events`, input));

/** Tạo "HP GALA 2026" (bản nháp) kèm 10 hạng mục + 4 khối mẫu. */
export const createFromWeb2026 = () =>
  unwrap<GalaEvent>(AxiosUtils.post(`${API.galaAdmin}/events/from-web-2026`));

export const getEvent = (id: number | string) => unwrap<GalaEventDetail>(AxiosUtils.get(base(id)));

export const updateEvent = (id: number | string, input: EventInput) =>
  unwrap<GalaEvent>(AxiosUtils.put(base(id), input));

export const setStatus = (id: number | string, status: GalaEventStatus) =>
  unwrap<GalaEvent>(AxiosUtils.patch(`${base(id)}/status`, { status }));

export const setShowOnHome = (id: number | string, showOnHome: boolean) =>
  unwrap<GalaEvent>(AxiosUtils.patch(`${base(id)}/home`, { showOnHome }));

/**
 * Số vé đã phát / đã vào cho cột trong danh sách. Danh sách sự kiện của server không kèm số này,
 * nên hỏi API vé (limit=1, chỉ lấy `counts`). Lỗi -> null (hiện "—", không chặn cả bảng).
 */
export async function getEventCounts(id: number | string): Promise<EventCounts | null> {
  try {
    const res: any = await AxiosUtils.get(`${base(id)}/tickets`, { page: 1, limit: 1 });
    const counts = res?.data?.counts;
    if (!counts) return null;
    return { issued: Number(counts.issued) || 0, checkedIn: Number(counts.checkedIn) || 0 };
  } catch {
    return null;
  }
}

// ---------- Khối nội dung ----------

export const listBlocks = (id: number | string) =>
  unwrap<GalaBlock[]>(AxiosUtils.get(`${base(id)}/blocks`));

/** Có `blockId` thì sửa (PUT), không thì tạo mới (POST). */
export const saveBlock = (id: number | string, input: BlockInput, blockId?: number | null) =>
  blockId
    ? unwrap<GalaBlock>(AxiosUtils.put(`${base(id)}/blocks/${blockId}`, input))
    : unwrap<GalaBlock>(AxiosUtils.post(`${base(id)}/blocks`, input));

export const deleteBlock = (id: number | string, blockId: number) =>
  AxiosUtils._delete(`${base(id)}/blocks/${blockId}`, undefined);

export const reorderBlocks = (id: number | string, ids: number[]) =>
  unwrap<GalaBlock[]>(AxiosUtils.put(`${base(id)}/blocks/reorder`, { ids }));

// ---------- Hạng mục ----------

export const listCategories = (id: number | string) =>
  unwrap<GalaCategory[]>(AxiosUtils.get(`${base(id)}/categories`));

/** Có `categoryId` thì sửa (PUT), không thì tạo mới (POST). */
export const saveCategory = (
  id: number | string,
  input: Partial<CategoryInput>,
  categoryId?: number | null
) =>
  categoryId
    ? unwrap<GalaCategory>(AxiosUtils.put(`${base(id)}/categories/${categoryId}`, input))
    : unwrap<GalaCategory>(AxiosUtils.post(`${base(id)}/categories`, input));

export const deleteCategory = (id: number | string, categoryId: number) =>
  AxiosUtils._delete(`${base(id)}/categories/${categoryId}`, undefined);

export const reorderCategories = (id: number | string, ids: number[]) =>
  unwrap<GalaCategory[]>(AxiosUtils.put(`${base(id)}/categories/reorder`, { ids }));

/** Nạp 10 hạng mục (bỏ qua mã đã có) + 4 khối nếu sự kiện chưa có khối nào. */
export const seed2026 = (id: number | string) =>
  unwrap<SeedResult>(AxiosUtils.post(`${base(id)}/categories/seed-2026`));

// ---------- Mẫu vé ----------

export const listTemplates = (id: number | string) =>
  unwrap<GalaTemplate[]>(AxiosUtils.get(`${base(id)}/templates`));

/** Có `templateId` thì sửa (PUT), không thì tạo mới (POST). */
export const saveTemplate = (
  id: number | string,
  input: Partial<TemplateInput>,
  templateId?: number | null
) =>
  templateId
    ? unwrap<GalaTemplate>(AxiosUtils.put(`${base(id)}/templates/${templateId}`, input))
    : unwrap<GalaTemplate>(AxiosUtils.post(`${base(id)}/templates`, input));

export const deleteTemplate = (id: number | string, templateId: number) =>
  AxiosUtils._delete(`${base(id)}/templates/${templateId}`, undefined);

/** Vẽ thử mẫu với tên cho trước + QR giả; trả PNG dạng data URL. */
export const previewTemplate = (id: number | string, templateId: number, name?: string) =>
  unwrap<{ pngDataUrl: string }>(
    AxiosUtils.post(`${base(id)}/templates/${templateId}/preview`, name ? { name } : {})
  );

/**
 * Tải ảnh mẫu lên kho ảnh sẵn có (POST /image/storages, trường 'attachment') và trả KHOÁ 'hp/<uuid>' —
 * giá trị mà `imageKey` của mẫu nhận. Lỗi thì ném `{ message }` (đọc bằng errorText).
 */
export async function uploadTemplateImage(file: File | Blob): Promise<string> {
  const form = new FormData();
  form.append('attachment', file);
  const res: any = await AxiosUtils.post(API.uploadFile, form, {
    'Content-Type': 'multipart/form-data',
  });
  const key = res?.data?.url;
  if (typeof key !== 'string' || !key) {
    throw new Error('Tải ảnh lên không thành công, vui lòng thử lại.');
  }
  return key;
}

// ---------- Vé / khách mời ----------

export const listTickets = (id: number | string, query: TicketQuery) =>
  unwrap<TicketList>(AxiosUtils.get(`${base(id)}/tickets`, query)); // `seated` (0|1) đi cùng query

export const pasteGuests = (id: number | string, text: string) =>
  unwrap<UpsertResult>(AxiosUtils.post(`${base(id)}/tickets/paste`, { text }));

export const importGuestSheet = (id: number | string, file: File | Blob) => {
  const form = new FormData();
  form.append('file', file);
  return unwrap<UpsertResult>(
    AxiosUtils.post(`${base(id)}/tickets/import`, form, { 'Content-Type': 'multipart/form-data' })
  );
};

/** Tối đa 50 ảnh một lần; tên file = UID TikTok. */
export const importGuestImages = (id: number | string, files: (File | Blob)[]) => {
  const form = new FormData();
  files.forEach((f) => form.append('files', f));
  return unwrap<UpsertResult>(
    AxiosUtils.post(`${base(id)}/tickets/images`, form, { 'Content-Type': 'multipart/form-data' })
  );
};

export const addGuestsFromUsers = (id: number | string, userIds: number[]) =>
  unwrap<UpsertResult>(AxiosUtils.post(`${base(id)}/tickets/from-users`, { userIds }));

export const updateTicket = (id: number | string, ticketId: number, input: TicketUpdateInput) =>
  unwrap<unknown>(AxiosUtils.put(`${base(id)}/tickets/${ticketId}`, input));

export const linkTicket = (id: number | string, ticketId: number, userId: number) =>
  unwrap<unknown>(AxiosUtils.post(`${base(id)}/tickets/${ticketId}/link`, { userId }));

export const revokeTicket = (id: number | string, ticketId: number, reason: string) =>
  unwrap<unknown>(AxiosUtils.post(`${base(id)}/tickets/${ticketId}/revoke`, { reason }));

export const restoreTicket = (id: number | string, ticketId: number) =>
  unwrap<unknown>(AxiosUtils.post(`${base(id)}/tickets/${ticketId}/restore`));

export const rerenderTicket = (id: number | string, ticketId: number) =>
  unwrap<unknown>(AxiosUtils.post(`${base(id)}/tickets/${ticketId}/rerender`));

export const deleteTicket = (id: number | string, ticketId: number) =>
  AxiosUtils._delete(`${base(id)}/tickets/${ticketId}`, undefined);

/** Xếp vé ISSUED chưa có ảnh (hoặc tất cả khi force) vào hàng đợi vẽ. */
export const renderAll = (id: number | string, force = false) =>
  unwrap<unknown>(AxiosUtils.post(`${base(id)}/tickets/render-all`, { force }));

export const getRenderProgress = (id: number | string) =>
  unwrap<RenderProgress>(AxiosUtils.get(`${base(id)}/tickets/render-progress`));

/** Xuất toàn bộ vé ra Excel: trả blob + tên file gợi ý của server. */
export async function exportTickets(
  id: number | string
): Promise<{ blob: Blob; filename: string }> {
  const res: any = await AxiosUtils.get(`${base(id)}/tickets/export.xlsx`, undefined, undefined, {
    responseType: 'blob',
    timeout: 120_000,
  });
  const disposition: string = res?.headers?.['content-disposition'] ?? '';
  const match = /filename="?([^";]+)"?/i.exec(disposition);
  return {
    blob: new Blob([res.data], {
      type:
        res?.headers?.['content-type'] ||
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    }),
    filename: match ? match[1] : `gala-${id}-ve-moi.xlsx`,
  };
}

// ---------- Check-in (Task 15) ----------

export const listCheckins = (id: number | string, query: CheckinQuery) =>
  unwrap<CheckinList>(AxiosUtils.get(`${base(id)}/checkins`, query));

/** Admin nhập mã tay 6 ký tự để check-in từ máy tính. */
export const manualCheckin = (id: number | string, shortCode: string) =>
  unwrap<ManualCheckinResult>(AxiosUtils.post(`${base(id)}/checkins/manual`, { shortCode }));

export const undoCheckin = (id: number | string, checkinId: number, reason: string) =>
  unwrap<{ ok: true }>(AxiosUtils.post(`${base(id)}/checkins/${checkinId}/undo`, { reason }));

export const listStaff = (id: number | string) =>
  unwrap<StaffBrief[]>(AxiosUtils.get(`${base(id)}/staff`));

export const saveStaff = (id: number | string, userIds: number[]) =>
  unwrap<StaffBrief[]>(AxiosUtils.put(`${base(id)}/staff`, { userIds }));

// ---------- Nhắc lịch (Task 15) ----------

export const listReminders = (id: number | string) =>
  unwrap<GalaReminder[]>(AxiosUtils.get(`${base(id)}/reminders`));

export const createReminder = (id: number | string, input: ReminderInput) =>
  unwrap<GalaReminder>(AxiosUtils.post(`${base(id)}/reminders`, input));

export const updateReminder = (id: number | string, reminderId: number, input: ReminderInput) =>
  unwrap<GalaReminder>(AxiosUtils.put(`${base(id)}/reminders/${reminderId}`, input));

export const deleteReminder = (id: number | string, reminderId: number) =>
  AxiosUtils._delete(`${base(id)}/reminders/${reminderId}`, undefined);

export const sendReminderNow = (id: number | string, input: SendNowInput) =>
  unwrap<GalaReminder>(AxiosUtils.post(`${base(id)}/reminders/send-now`, input));

export const resetReminderDefaults = (id: number | string) =>
  unwrap<GalaReminder[]>(AxiosUtils.post(`${base(id)}/reminders/reset-defaults`));

// ---------- Đề cử & giải (đợt 3a) ----------

export const listNominees = (id: number | string) =>
  unwrap<NomineeOverview>(AxiosUtils.get(`${base(id)}/nominees`));

export const createNominee = (id: number | string, categoryId: number, input: NomineeCreateInput) =>
  unwrap<GalaNominee>(AxiosUtils.post(`${base(id)}/categories/${categoryId}/nominees`, input));

export const updateNominee = (id: number | string, nomineeId: number, input: NomineeUpdateInput) =>
  unwrap<GalaNominee>(AxiosUtils.put(`${base(id)}/nominees/${nomineeId}`, input));

/** Thay toàn bộ danh sách vé thành viên của đơn vị. */
export const setNomineeMembers = (id: number | string, nomineeId: number, ticketIds: number[]) =>
  unwrap<GalaNominee>(AxiosUtils.put(`${base(id)}/nominees/${nomineeId}/members`, { ticketIds }));

export const deleteNominee = (id: number | string, nomineeId: number) =>
  AxiosUtils._delete(`${base(id)}/nominees/${nomineeId}`, undefined);

/** Dán danh sách đề cử vào một hạng mục: mỗi dòng `UID[, tên đơn vị]`. */
export const pasteNominees = (id: number | string, categoryId: number, text: string) =>
  unwrap<NomineePasteResult>(
    AxiosUtils.post(`${base(id)}/categories/${categoryId}/nominees/paste`, { text })
  );

export const announceNominees = (id: number | string, categoryId: number) =>
  unwrap<AnnounceResult>(AxiosUtils.post(`${base(id)}/categories/${categoryId}/announce-nominees`));

export const unannounceNominees = (id: number | string, categoryId: number) =>
  unwrap<unknown>(AxiosUtils.post(`${base(id)}/categories/${categoryId}/unannounce-nominees`));

export const announceWinners = (id: number | string, categoryId: number) =>
  unwrap<AnnounceResult>(AxiosUtils.post(`${base(id)}/categories/${categoryId}/announce-winners`));

export const unannounceWinners = (id: number | string, categoryId: number) =>
  unwrap<unknown>(AxiosUtils.post(`${base(id)}/categories/${categoryId}/unannounce-winners`));

/** Công bố mọi hạng mục đủ điều kiện; hạng mục bị bỏ qua kèm lý do. */
export const announceAll = (id: number | string, stage: AnnounceStage) =>
  unwrap<AnnounceAllResult>(AxiosUtils.post(`${base(id)}/nominees/announce-all`, { stage }));

// ---------- Sơ đồ chỗ ngồi (GALA đợt 2a) ----------

export const getSeating = (id: number | string) =>
  unwrap<AdminSeatingView>(AxiosUtils.get(`${base(id)}/seating`));

export const createTable = (id: number | string, input: TableInput) =>
  unwrap<TableWithSeats>(AxiosUtils.post(`${base(id)}/seating/tables`, input));

/** Thêm nhanh `count` bàn tròn 10 ghế tên "{prefix} {k}". */
export const bulkCreateTables = (id: number | string, input: BulkTablesInput) =>
  unwrap<TableWithSeats[]>(AxiosUtils.post(`${base(id)}/seating/tables/bulk`, input));

export const updateTable = (id: number | string, tableId: number, input: Partial<TableInput>) =>
  unwrap<TableWithSeats>(AxiosUtils.put(`${base(id)}/seating/tables/${tableId}`, input));

/** Chỉ xoá được bàn trống. */
export const deleteTable = (id: number | string, tableId: number) =>
  AxiosUtils._delete(`${base(id)}/seating/tables/${tableId}`, undefined);

export const reorderTables = (id: number | string, ids: number[]) =>
  unwrap<{ ok: true }>(AxiosUtils.put(`${base(id)}/seating/tables/reorder`, { ids }));

/** Dán danh sách (mỗi dòng một UID TikTok hoặc tên) vào các ghế trống của bàn. */
export const pasteIntoTable = (id: number | string, tableId: number, text: string) =>
  unwrap<PasteSeatResult>(AxiosUtils.post(`${base(id)}/seating/tables/${tableId}/paste`, { text }));

/** Gán / chuyển (ticketId), tên tay (guestName) hoặc bỏ ghế (cả hai null). */
export const assignSeat = (id: number | string, seatId: number, input: SeatAssignInput) =>
  unwrap<AdminSeat>(AxiosUtils.put(`${base(id)}/seating/seats/${seatId}`, input));

/** Công bố / ẩn sơ đồ với người có vé. */
export const setSeatingVisible = (id: number | string, visible: boolean) =>
  unwrap<{ visible: boolean }>(AxiosUtils.patch(`${base(id)}/seating/visible`, { visible }));

/** Xuất sơ đồ ra Excel (mỗi ghế một dòng, kể cả ghế trống): trả blob + tên file gợi ý của server. */
export async function exportSeating(
  id: number | string
): Promise<{ blob: Blob; filename: string }> {
  const res: any = await AxiosUtils.get(`${base(id)}/seating/export.xlsx`, undefined, undefined, {
    responseType: 'blob',
    timeout: 120_000,
  });
  const disposition: string = res?.headers?.['content-disposition'] ?? '';
  const match = /filename="?([^";]+)"?/i.exec(disposition);
  return {
    blob: new Blob([res.data], {
      type:
        res?.headers?.['content-type'] ||
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    }),
    filename: match ? match[1] : `gala-${id}-cho-ngoi.xlsx`,
  };
}
